import { useRef, useState, useEffect } from "react";
import { labels } from "../design/tokens";

export interface DateValue {
  day: string;
  month: string;
  year: string;
}

interface Props {
  value: DateValue;
  onChange: (v: DateValue) => void;
  label?: string;
  helper?: string;
  disallowFuture?: boolean;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
}

export default function DateInput({
  value,
  onChange,
  label,
  helper,
  disallowFuture = false,
  dayPlaceholder = "DD",
  monthPlaceholder = "MM",
  yearPlaceholder = "YYYY",
}: Props) {
  const val = value ?? { day: "", month: "", year: "" };
  const [futureError, setFutureError] = useState(false);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const handleDay = (v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 2);
    onChange({ ...val, day: clean });
    if (clean.length === 2) monthRef.current?.focus();
    setFutureError(false);
  };
  const handleMonth = (v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 2);
    onChange({ ...val, month: clean });
    if (clean.length === 2) yearRef.current?.focus();
    setFutureError(false);
  };
  const handleYear = (v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 4);
    onChange({ ...val, year: clean });
    setFutureError(false);
  };

  useEffect(() => {
    if (disallowFuture && val.day && val.month && val.year.length === 4) {
      const date = new Date(parseInt(val.year, 10), parseInt(val.month, 10) - 1, parseInt(val.day, 10));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date > today) {
        setFutureError(true);
        onChange({ ...val, year: "" });
        yearRef.current?.focus();
      }
    }
  }, [val.day, val.month, val.year, disallowFuture, val, onChange]);

  const isValid = val.day && val.month && val.year.length === 4;

  return (
    <div>
      {label && (
        <label className={`${labels.field} mb-1 block`}>
          {label}
        </label>
      )}
      <div className="overflow-hidden w-full" style={{ boxSizing: "border-box" }}>
        <div
          className={`flex items-center bg-white border rounded-lg overflow-hidden transition-colors ${
            isValid ? "border-emerald-400" : "border-slate-200 focus-within:border-emerald-400"
          }`}
          style={{ maxWidth: "min(260px, 100%)" }}
        >
          <input type="text" inputMode="numeric" value={val.day} onChange={(e) => handleDay(e.target.value)}
            placeholder={dayPlaceholder} maxLength={2}
            className="flex-1 min-w-0 h-10 text-center text-sm text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none" />
          <span className="w-3 flex-shrink-0 text-center text-slate-300 text-sm">/</span>
          <input ref={monthRef} type="text" inputMode="numeric" value={val.month} onChange={(e) => handleMonth(e.target.value)}
            placeholder={monthPlaceholder} maxLength={2}
            className="flex-1 min-w-0 h-10 text-center text-sm text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none" />
          <span className="w-3 flex-shrink-0 text-center text-slate-300 text-sm">/</span>
          <input ref={yearRef} type="text" inputMode="numeric" value={val.year} onChange={(e) => handleYear(e.target.value)}
            placeholder={yearPlaceholder} maxLength={4}
            className="flex-[1.2] min-w-0 h-10 text-center text-sm text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none" />
          {isValid && (
            <div className="pr-2 flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
            </div>
          )}
        </div>
      </div>
      {futureError && <p className="text-[11px] text-rose-500 mt-1">Date cannot be in the future</p>}
      {helper && !futureError && <p className="text-[11px] text-slate-500 mt-1">{helper}</p>}
    </div>
  );
}
